import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Users, Search, Phone, Mail, MapPin, ShoppingCart, DollarSign, X } from 'lucide-react';
import './Clientes.css';

const Clientes = () => {
  const [busca, setBusca] = useState('');
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [crediarioForm, setCrediarioForm] = useState({
    crediarioHabilitado: false,
    crediarioBloqueado: false,
    crediarioDataVencimento: ''
  });
  const queryClient = useQueryClient();

  const { data: estabelecimento } = useQuery(
    'estabelecimento-clientes',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data.estabelecimento;
      } catch (error) {
        if (error.response?.status === 404) {
          const response = await api.get('/api/estabelecimentos/padrao');
          return response.data.data.estabelecimento;
        }
        return null;
      }
    }
  );

  const { data: clientesData, isLoading } = useQuery(
    ['clientes', estabelecimento?._id, busca],
    async () => {
      if (!estabelecimento?._id) return { clientes: [] };
      const params = new URLSearchParams({ estabelecimento: estabelecimento._id });
      if (busca) params.append('busca', busca);
      const response = await api.get(`/api/clientes?${params}`);
      return response.data.data;
    },
    {
      enabled: !!estabelecimento?._id
    }
  );

  const clientes = clientesData?.clientes || [];

  useEffect(() => {
    if (!clienteSelecionado) return;
    const toInputDate = (d) => {
      if (!d) return '';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return '';
      return dt.toISOString().split('T')[0];
    };

    setCrediarioForm({
      crediarioHabilitado: clienteSelecionado.crediarioHabilitado === true,
      crediarioBloqueado: clienteSelecionado.crediarioBloqueado === true,
      crediarioDataVencimento: toInputDate(clienteSelecionado.crediarioDataVencimento)
    });
  }, [clienteSelecionado]);

  const atualizarCrediarioMutation = useMutation(
    async ({ clienteId, payload }) => {
      return api.patch(`/api/clientes/${clienteId}/crediario`, payload);
    },
    {
      onSuccess: (response) => {
        const cli = response?.data?.data?.cliente;
        if (cli) {
          setClienteSelecionado(cli);
        }
        queryClient.invalidateQueries('clientes');
      }
    }
  );

  const stats = [
    {
      title: 'Total de Clientes',
      value: clientes.length,
      icon: Users,
      color: 'premium-blue'
    },
    {
      title: 'Com Pedidos',
      value: clientes.filter(c => c.totalPedidos > 0).length,
      icon: ShoppingCart,
      color: 'premium-green'
    },
    {
      title: 'Total Gasto',
      value: `R$ ${clientes.reduce((acc, c) => acc + (c.totalGasto || 0), 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'premium-purple'
    }
  ];

  return (
    <div className="clientes-page">
      <div className="page-header">
        <div>
          <h2><Users size={28} /> Gestão de Clientes</h2>
          <p>Gerencie seus clientes e visualize histórico de compras</p>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="stats-grid-clientes">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`stat-card-clientes ${stat.color}`}>
              <div className="stat-icon-clientes">
                <Icon size={24} />
              </div>
              <div className="stat-content-clientes">
                <p className="stat-title-clientes">{stat.title}</p>
                <p className="stat-value-clientes">{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Busca */}
      <div className="search-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por nome, telefone ou email..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      {/* Lista de Clientes */}
      {isLoading ? (
        <div className="loading-section">
          <p>Carregando clientes...</p>
        </div>
      ) : clientes.length === 0 ? (
        <div className="empty-state">
          <Users size={64} />
          <p>Nenhum cliente encontrado</p>
          <span>Os clientes serão cadastrados automaticamente ao fazerem pedidos</span>
        </div>
      ) : (
        <div className="clientes-grid">
          {clientes.map(cliente => (
            <div key={cliente._id} className="cliente-card">
              <div className="cliente-header">
                <div className="cliente-avatar">
                  {cliente.nome?.charAt(0).toUpperCase() || 'C'}
                </div>
                <div className="cliente-info-header">
                  <h3>{cliente.nome || 'Cliente sem nome'}</h3>
                  <span className="cliente-badge">
                    {cliente.totalPedidos || 0} pedido(s)
                  </span>
                </div>
              </div>

              <div className="cliente-detalhes">
                <div className="detalhe-item">
                  <Phone size={16} />
                  <span>{cliente.telefone || 'Não informado'}</span>
                </div>
                {cliente.email && (
                  <div className="detalhe-item">
                    <Mail size={16} />
                    <span>{cliente.email}</span>
                  </div>
                )}
                {cliente.enderecos && cliente.enderecos.length > 0 && (
                  <div className="detalhe-item">
                    <MapPin size={16} />
                    <span>
                      {cliente.enderecos[0].rua}, {cliente.enderecos[0].numero}
                    </span>
                  </div>
                )}
              </div>

              <div className="cliente-stats">
                <div className="stat-mini">
                  <span>Total Gasto</span>
                  <strong>R$ {(cliente.totalGasto || 0).toFixed(2)}</strong>
                </div>
                <div className="stat-mini">
                  <span>Último Pedido</span>
                  <strong>
                    {cliente.ultimoPedido 
                      ? new Date(cliente.ultimoPedido).toLocaleDateString('pt-BR')
                      : 'Nunca'}
                  </strong>
                </div>
              </div>

              <button 
                className="btn-ver-detalhes"
                onClick={() => setClienteSelecionado(cliente)}
              >
                Ver Detalhes
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Detalhes */}
      {clienteSelecionado && (
        <div className="modal-overlay" onClick={() => setClienteSelecionado(null)}>
          <div className="modal-content modal-cliente" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setClienteSelecionado(null)}>
              <X size={24} />
            </button>
            <h3>Detalhes do Cliente</h3>
            <div className="cliente-detalhes-modal">
              <div className="info-group">
                <label>Nome</label>
                <p>{clienteSelecionado.nome}</p>
              </div>
              <div className="info-group">
                <label>Telefone</label>
                <p>{clienteSelecionado.telefone}</p>
              </div>
              {clienteSelecionado.email && (
                <div className="info-group">
                  <label>Email</label>
                  <p>{clienteSelecionado.email}</p>
                </div>
              )}
              <div className="info-group">
                <label>Total de Pedidos</label>
                <p>{clienteSelecionado.totalPedidos || 0}</p>
              </div>
              <div className="info-group">
                <label>Total Gasto</label>
                <p>R$ {(clienteSelecionado.totalGasto || 0).toFixed(2)}</p>
              </div>
              {clienteSelecionado.enderecos && clienteSelecionado.enderecos.length > 0 && (
                <div className="info-group">
                  <label>Endereços</label>
                  {clienteSelecionado.enderecos.map((end, idx) => (
                    <div key={idx} className="endereco-item">
                      <p>{end.rua}, {end.numero}</p>
                      <p>{end.bairro} - {end.cidade}/{end.estado}</p>
                      {end.cep && <p>CEP: {end.cep}</p>}
                    </div>
                  ))}
                </div>
              )}

              <div className="info-group">
                <label>Crediário</label>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={crediarioForm.crediarioHabilitado === true}
                      onChange={(e) => setCrediarioForm((prev) => ({ ...prev, crediarioHabilitado: e.target.checked }))}
                    />
                    {' '}Permitir crediário
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={crediarioForm.crediarioBloqueado === true}
                      onChange={(e) => setCrediarioForm((prev) => ({ ...prev, crediarioBloqueado: e.target.checked }))}
                    />
                    {' '}Bloquear crediário
                  </label>

                  <div style={{ display: 'grid', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>Data de vencimento</span>
                    <input
                      type="date"
                      value={crediarioForm.crediarioDataVencimento || ''}
                      onChange={(e) => setCrediarioForm((prev) => ({ ...prev, crediarioDataVencimento: e.target.value }))}
                    />
                  </div>

                  <button
                    className="btn-ver-detalhes"
                    onClick={() => {
                      atualizarCrediarioMutation.mutate({
                        clienteId: clienteSelecionado._id,
                        payload: {
                          crediarioHabilitado: crediarioForm.crediarioHabilitado === true,
                          crediarioBloqueado: crediarioForm.crediarioBloqueado === true,
                          crediarioDataVencimento: crediarioForm.crediarioDataVencimento
                        }
                      });
                    }}
                    disabled={atualizarCrediarioMutation.isLoading}
                    type="button"
                  >
                    {atualizarCrediarioMutation.isLoading ? 'Salvando...' : 'Salvar Crediário'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;

